import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from "../api/config";

import { useNavigate } from "react-router-dom";
import { ArrowLeft, Upload } from 'lucide-react';

import { showSuccess, showError, showInfo, redirectPage } from "../utils/notification";
import { useAuth } from "../auth/AuthContext";

// Cascading location master used by the registration form.
// Shape: { District: { Taluk: { Block: [village, …] } } }
// This will be replaced by a live master API once the department provides one.
const LOCATION_MASTER = {
  Tiruvallur: {
    Poonamallee: {
      Thiruverkadu: ['Ayanambakkam', 'Sundarasolavaram', 'Koladi'],
      Poonamallee:  ['Kattupakkam', 'Kumananchavadi', 'Karayanchavadi'],
    },
    Ambattur: {
      Ambattur:   ['Avadi', 'Pattabiram', 'Mogappair'],
      Ponneri:    ['Ponneri Town', 'Pulicat', 'Minjur'],
    },
    Tiruvallur: {
      Tiruvallur: ['Tiruvallur Town', 'Tiruvalangadu', 'Manavur'],
    },
  },
  Chennai: {
    Egmore: {
      Egmore:     ['Egmore', 'Chetpet', 'Kilpauk'],
      Mylapore:   ['Mylapore', 'Triplicane', 'Royapettah'],
    },
    Tambaram: {
      Tambaram:   ['Tambaram Town', 'Selaiyur', 'Chitlapakkam'],
      Pallavaram: ['Pallavaram', 'Anakaputhur', 'Cowl Bazaar'],
    },
  },
  Chengalpattu: {
    Chengalpattu: {
      Chengalpattu: ['Chengalpattu Town', 'Singaperumal Koil'],
      Madurantakam: ['Madurantakam Town', 'Acharapakkam'],
    },
    Tirukalukundram: {
      Tirukalukundram: ['Tirukalukundram', 'Mamallapuram'],
    },
  },
  Kanchipuram: {
    Kanchipuram: {
      Kanchipuram: ['Kanchipuram Town', 'Walajabad'],
      Sriperumbudur: ['Sriperumbudur', 'Oragadam'],
    },
    Uthiramerur: {
      Uthiramerur: ['Uthiramerur Town'],
    },
  },
  Vellore: {
    Vellore: {
      Vellore:    ['Vellore Town', 'Katpadi', 'Sathuvachari'],
      Anaicut:    ['Anaicut', 'Kalavai'],
    },
    Gudiyatham: {
      Gudiyatham: ['Gudiyatham', 'Pernampet'],
    },
  },
  Madurai: {
    Madurai: {
      Madurai:    ['Madurai Town', 'Anna Nagar', 'KK Nagar'],
      Tirumangalam: ['Tirumangalam', 'Kallikudi'],
    },
    Usilampatti: {
      Usilampatti: ['Usilampatti Town', 'Sedapatti'],
    },
  },
};

export default function RegisterPage() {
  // FRS Section I (1): Applicant Type — NGOs/Institution or Individual (non-organizational)
  const [userType, setUserType] = useState('NGO');
  const [formData, setFormData] = useState({
    email_id: '',
    password: '',
    full_name: '',
    mobile_number: '',
    pan_number: '',
    gst_number: '',
    contact_person_name: '',
    contact_person_email: '',
    // FRS additions
    founder_promoter_name: '', // mandatory for NGO/Institution
    proponent_name: '',        // mandatory for both — site coordinator
    aadhaar_number: '',        // mandatory for Individual (12 digits)
    address: '',
    district: '',
    taluk: '',
    block: '',
    village: '',
    pin_code: '',
  });

  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/dashboard");
    }
  }, [navigate]);

  const [uploadedFiles, setUploadedFiles] = useState({
    registration_cretificate_path: null,
    pan_card_path: null,
    organization_profile_path: null,
    similar_project_details_path: null,
    resolution_copy_path: null,
  });

  const isNGO = userType === 'NGO';

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileUpload = (fileType, file) => {
    setUploadedFiles(prev => ({ ...prev, [fileType]: file }));
  };

  const validate = () => {
    if (!formData.email_id || !formData.password || !formData.full_name) {
      showError("Please fill in all required fields.");
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email_id)) {
      showError("Please enter a valid email address.");
      return false;
    }

    // Strong password policy (pen-test fix 5.3). Server re-validates.
    const pwd = formData.password || '';
    if (pwd.length < 8) {
      showError('Password must be at least 8 characters long.');
      return false;
    }
    if (!/[a-z]/.test(pwd)) { showError('Password must contain at least one lowercase letter.'); return false; }
    if (!/[A-Z]/.test(pwd)) { showError('Password must contain at least one uppercase letter.'); return false; }
    if (!/[0-9]/.test(pwd)) { showError('Password must contain at least one digit.'); return false; }
    if (!/[^A-Za-z0-9]/.test(pwd)) { showError('Password must contain at least one special character.'); return false; }
    const localPart = String(formData.email_id).split('@')[0]?.toLowerCase() || '';
    if (localPart.length >= 3 && pwd.toLowerCase().includes(localPart)) {
      showError('Password must not contain your email address.');
      return false;
    }

    if (isNGO) {
      if (!formData.founder_promoter_name) { showError("Founder / Promoter Name is required for NGO/Institution."); return false; }
      if (!formData.proponent_name) { showError("Name of Proponent is required."); return false; }
      if (!formData.mobile_number) { showError("Mobile Number is required."); return false; }
      if (!formData.pan_number) { showError("PAN Number is required for NGO/Institution."); return false; }
      if (!uploadedFiles.pan_card_path) { showError("PAN Card is required for NGO/Institution."); return false; }
    } else {
      if (!formData.proponent_name) { showError("Name of Proponent is required."); return false; }
      if (!formData.mobile_number) { showError("Mobile Number is required."); return false; }
      if (!formData.aadhaar_number) { showError("Aadhaar Number is required for Individual applicants."); return false; }
      if (!/^\d{12}$/.test(formData.aadhaar_number)) { showError("Aadhaar Number must be 12 digits."); return false; }
    }

    // Location is mandatory per FRS (cascading: District → Taluk → Block → Village)
    if (!formData.address) { showError("Address is required."); return false; }
    if (!formData.district) { showError("District is required."); return false; }
    if (!formData.taluk) { showError("Taluk is required."); return false; }
    if (!formData.block) { showError("Block is required."); return false; }
    if (!formData.village) { showError("Village is required."); return false; }
    if (!formData.pin_code || !/^\d{6}$/.test(formData.pin_code)) { showError("Pin Code must be 6 digits."); return false; }

    if (formData.pan_number && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(formData.pan_number)) {
      showError("Please enter a valid PAN number.");
      return false;
    }

    if (formData.gst_number && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(formData.gst_number)) {
      showError("Please enter a valid GST number.");
      return false;
    }

    if (formData.mobile_number && !/^\d{10}$/.test(formData.mobile_number)) {
      showError("Please enter a valid 10-digit phone number.");
      return false;
    }

    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    const submitFormData = new FormData();
    submitFormData.append("user_type", userType);
    Object.keys(formData).forEach(key => {
      if (formData[key] !== '' && formData[key] !== null && formData[key] !== undefined) {
        submitFormData.append(key, formData[key]);
      }
    });
    Object.keys(uploadedFiles).forEach(fileKey => {
      if (uploadedFiles[fileKey]) {
        submitFormData.append(fileKey, uploadedFiles[fileKey]);
      }
    });

    axios.post(`${API_BASE_URL}/users`, submitFormData, {
      headers: { "Content-Type": "multipart/form-data" }
    })
      .then(response => {
        if (response.data && response.data.success === true) {
          showSuccess(response.data.message || "Registration successful! Please log in.");
          redirectPage("/login");
        } else {
          showError(response.data?.message || "Registration failed.");
        }
      })
      .catch(error => {
        showError(error?.response?.data?.message || "Registration failed. Please try again.");
      });
  };

  const handleBack = () => {
    window.location.href = '/login';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-100 p-2 sm:p-4 lg:p-6">
      <div className="max-w-6xl mx-auto">

        {/* Header */}
        <div className="w-full px-3 sm:px-6 py-4 sm:py-6 mb-4 sm:mb-6">
          <div className="flex items-center justify-between sm:justify-center relative">
            <button
              onClick={handleBack}
              className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 transition-colors sm:absolute sm:left-0"
            >
              <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="text-sm font-medium hidden sm:inline">Back to Login</span>
              <span className="text-xs font-medium sm:hidden">Back</span>
            </button>

            <div className="flex items-center space-x-3 sm:space-x-4">
              <div className="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 flex-shrink-0">
                <img src="/images/tnlogo.png" alt="Tamil Nadu Government Logo" className="w-full h-full object-contain" />
              </div>
              <div className="text-left">
                <h1 className="text-sm sm:text-base lg:text-lg font-bold text-[#036FAA] font-['Poppins'] leading-tight">Tamil Nadu Government</h1>
                <h2 className="text-xs sm:text-sm text-[#036FAA] font-medium font-['Poppins'] leading-tight">NOC Application System</h2>
              </div>
            </div>
          </div>
        </div>

        {/* Main Form Container */}
        <div className="bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden">

          {/* Form Content */}
          <div className="p-4 sm:p-6 lg:p-8">
            <div className="space-y-8">

              {/* Basic Information */}
              <div>
                <div className="flex items-center mb-6">
                  <div className="w-1 h-6 bg-blue-600 rounded-full mr-3"></div>
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900">Basic Information</h3>
                </div>

                {/* Registration Type dropdown — replaces tabs */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Applicant Type <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={userType}
                      onChange={(e) => setUserType(e.target.value)}
                      className="w-full px-3 py-3 text-sm border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                    >
                      <option value="NGO">NGOs / Institution</option>
                      <option value="Others">Individual (non-organizational)</option>
                    </select>
                  </div>
                </div>

                {/* Conditional NGO-only fields per FRS Section I (2) & (3) */}
                {isNGO && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mt-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Founder / Promoter Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.founder_promoter_name}
                        onChange={(e) => handleInputChange('founder_promoter_name', e.target.value)}
                        maxLength={50}
                        className="w-full px-3 py-3 text-sm border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                        placeholder="Alphabets, max 50 chars"
                      />
                    </div>
                  </div>
                )}

                {/* Proponent Name — mandatory for both per FRS Section I (4) */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mt-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Name of Proponent <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.proponent_name}
                      onChange={(e) => handleInputChange('proponent_name', e.target.value)}
                      maxLength={50}
                      className="w-full px-3 py-3 text-sm border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                      placeholder="Site coordinator & point of contact"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Proponent acts as the project site coordinator and point of contact for the District Collector
                      and the Assistant Engineer (WRD).
                    </p>
                  </div>
                </div>

                {/* Aadhaar field — only for Individual applicants per FRS Section I (8) */}
                {!isNGO && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mt-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Aadhaar Number of Proponent <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.aadhaar_number}
                        onChange={(e) => handleInputChange('aadhaar_number', e.target.value.replace(/\D/g, '').slice(0, 12))}
                        maxLength={12}
                        className="w-full px-3 py-3 text-sm border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                        placeholder="12 digit Aadhaar"
                      />
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mt-6">
                  {/* Email */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      value={formData.email_id}
                      onChange={(e) => handleInputChange('email_id', e.target.value)}
                      className="w-full px-3 py-3 text-sm border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                      placeholder="Enter email address"
                    />
                  </div>

                  {/* Password */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Password <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="password"
                      value={formData.password}
                      onChange={(e) => handleInputChange('password', e.target.value)}
                      className="w-full px-3 py-3 text-sm border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                      placeholder="Create password"
                      minLength={8}
                      maxLength={128}
                    />
                    {/* Password-policy live hints (pen-test fix 5.3) */}
                    <ul className="mt-2 text-xs space-y-0.5">
                      {(() => {
                        const pwd = formData.password || '';
                        const rules = [
                          { ok: pwd.length >= 8,        text: 'At least 8 characters' },
                          { ok: /[a-z]/.test(pwd),      text: 'One lowercase letter' },
                          { ok: /[A-Z]/.test(pwd),      text: 'One uppercase letter' },
                          { ok: /[0-9]/.test(pwd),      text: 'One digit' },
                          { ok: /[^A-Za-z0-9]/.test(pwd), text: 'One special character (e.g. @ # $ !)' },
                        ];
                        return rules.map((r, i) => (
                          <li key={i} className={r.ok ? 'text-emerald-600' : 'text-gray-500'}>
                            {r.ok ? '✓' : '○'} {r.text}
                          </li>
                        ));
                      })()}
                    </ul>
                  </div>

                  {/* Full / Org Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {isNGO ? 'Organization Name' : 'Full Name'} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.full_name}
                      onChange={(e) => handleInputChange('full_name', e.target.value)}
                      className="w-full px-3 py-3 text-sm border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                      placeholder={isNGO ? 'Enter organization name' : 'Enter full name'}
                    />
                  </div>

                  {/* Phone */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      value={formData.mobile_number}
                      onChange={(e) => handleInputChange('mobile_number', e.target.value)}
                      className="w-full px-3 py-3 text-sm border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                      placeholder="10-digit phone number"
                    />
                  </div>

                  {/* PAN */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      PAN Number {isNGO ? <span className="text-red-500">*</span> : <span className="text-gray-500">(Optional)</span>}
                    </label>
                    <input
                      type="text"
                      value={formData.pan_number}
                      onChange={(e) => handleInputChange('pan_number', e.target.value.toUpperCase())}
                      className="w-full px-3 py-3 text-sm border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                      placeholder="Enter PAN number"
                    />
                  </div>

                  {/* GST */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      GST Number <span className="text-gray-500">(Optional)</span>
                    </label>
                    <input
                      type="text"
                      value={formData.gst_number}
                      onChange={(e) => handleInputChange('gst_number', e.target.value.toUpperCase())}
                      className="w-full px-3 py-3 text-sm border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                      placeholder="Enter GST number"
                    />
                  </div>

                  {/* Contact Person Name (Others) */}
                  {!isNGO && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Contact Person Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.contact_person_name}
                        onChange={(e) => handleInputChange('contact_person_name', e.target.value)}
                        className="w-full px-3 py-3 text-sm border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                        placeholder="Contact person name"
                      />
                    </div>
                  )}

                  {/* Contact Person Email (Others) */}
                  {!isNGO && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Contact Person Email <span className="text-gray-500">(Optional)</span>
                      </label>
                      <input
                        type="email"
                        value={formData.contact_person_email}
                        onChange={(e) => handleInputChange('contact_person_email', e.target.value)}
                        className="w-full px-3 py-3 text-sm border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                        placeholder="Contact person email"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Address & Cascading Location — FRS Section I (9–14) */}
              <div>
                <div className="flex items-center mb-6">
                  <div className="w-1 h-6 bg-blue-600 rounded-full mr-3"></div>
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900">Address & Location</h3>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                  <div className="sm:col-span-2 lg:col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Address <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={formData.address}
                      onChange={(e) => handleInputChange('address', e.target.value)}
                      maxLength={100}
                      rows={2}
                      className="w-full px-3 py-3 text-sm border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                      placeholder="Door / Street / Area (alphanumeric, max 100 chars)"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      District <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.district}
                      onChange={(e) => {
                        const v = e.target.value;
                        setFormData((p) => ({ ...p, district: v, taluk: '', block: '', village: '' }));
                      }}
                      className="w-full px-3 py-3 text-sm border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                    >
                      <option value="">— Select District —</option>
                      {Object.keys(LOCATION_MASTER).map((d) => (
                        <option key={d} value={d}>{d}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Taluk <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.taluk}
                      onChange={(e) => setFormData((p) => ({ ...p, taluk: e.target.value, block: '', village: '' }))}
                      disabled={!formData.district}
                      className="w-full px-3 py-3 text-sm border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all disabled:bg-gray-100"
                    >
                      <option value="">— Select Taluk —</option>
                      {formData.district && Object.keys(LOCATION_MASTER[formData.district] || {}).map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Block <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.block}
                      onChange={(e) => setFormData((p) => ({ ...p, block: e.target.value, village: '' }))}
                      disabled={!formData.taluk}
                      className="w-full px-3 py-3 text-sm border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all disabled:bg-gray-100"
                    >
                      <option value="">— Select Block —</option>
                      {formData.district && formData.taluk &&
                        Object.keys(LOCATION_MASTER[formData.district]?.[formData.taluk] || {}).map((b) => (
                          <option key={b} value={b}>{b}</option>
                        ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Village <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.village}
                      onChange={(e) => handleInputChange('village', e.target.value)}
                      disabled={!formData.block}
                      className="w-full px-3 py-3 text-sm border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all disabled:bg-gray-100"
                    >
                      <option value="">— Select Village —</option>
                      {formData.district && formData.taluk && formData.block &&
                        (LOCATION_MASTER[formData.district]?.[formData.taluk]?.[formData.block] || []).map((v) => (
                          <option key={v} value={v}>{v}</option>
                        ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Pin Code <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.pin_code}
                      onChange={(e) => handleInputChange('pin_code', e.target.value.replace(/\D/g, '').slice(0, 6))}
                      maxLength={6}
                      className="w-full px-3 py-3 text-sm border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                      placeholder="6 digits"
                    />
                  </div>
                </div>


              </div>

              {/* Document Upload Section */}
              <div>
                <div className="flex items-center mb-6">
                  <div className="w-1 h-6 bg-blue-600 rounded-full mr-3"></div>
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900">Document Upload</h3>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">

                  {isNGO && (
                    <>
                      {/* Registration Certificate */}
                      <FileField
                        id="registration_cretificate_path"
                        label="Registration Certificate"
                        required
                        file={uploadedFiles.registration_cretificate_path}
                        onChange={(f) => handleFileUpload('registration_cretificate_path', f)}
                      />
                      {/* PAN Card */}
                      <FileField
                        id="pan_card_path"
                        label="PAN Card Copy"
                        required
                        file={uploadedFiles.pan_card_path}
                        onChange={(f) => handleFileUpload('pan_card_path', f)}
                      />
                      {/* Organization Profile */}
                      <FileField
                        id="organization_profile_path"
                        label="Organization Profile"
                        required
                        file={uploadedFiles.organization_profile_path}
                        onChange={(f) => handleFileUpload('organization_profile_path', f)}
                      />
                      {/* Similar Projects */}
                      <FileField
                        id="similar_project_details_path"
                        label="Similar Project Details"
                        file={uploadedFiles.similar_project_details_path}
                        onChange={(f) => handleFileUpload('similar_project_details_path', f)}
                      />
                    </>
                  )}

                  {!isNGO && (
                    <>
                      {/* Resolution Copy */}
                      <FileField
                        id="resolution_copy_path"
                        label="Resolution Copy"
                        required
                        file={uploadedFiles.resolution_copy_path}
                        onChange={(f) => handleFileUpload('resolution_copy_path', f)}
                      />
                      {/* PAN Card (optional) */}
                      <FileField
                        id="pan_card_path"
                        label="PAN Card Copy"
                        file={uploadedFiles.pan_card_path}
                        onChange={(f) => handleFileUpload('pan_card_path', f)}
                      />
                      {/* Similar Projects */}
                      <FileField
                        id="similar_project_details_path"
                        label="Similar Project Details"
                        file={uploadedFiles.similar_project_details_path}
                        onChange={(f) => handleFileUpload('similar_project_details_path', f)}
                      />
                    </>
                  )}
                </div>
              </div>

              {/* Submit */}
              <div className="flex justify-center pt-6 border-t border-gray-100">
                <button
                  onClick={handleSubmit}
                  type="submit"
                  className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-8 sm:px-12 py-3 sm:py-4 rounded-lg font-semibold text-sm sm:text-base hover:from-blue-700 hover:to-blue-800 transition-all shadow-lg hover:shadow-xl transform hover:scale-105 active:scale-95 focus:outline-none focus:ring-4 focus:ring-blue-300"
                >
                  Submit Registration
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function FileField({ id, label, required, file, onChange }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-3">
        {label} {required ? <span className="text-red-500">*</span> : <span className="text-gray-500">(Optional)</span>}
      </label>
      <div className="relative">
        <input
          type="file"
          id={id}
          className="hidden"
          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
          onChange={(e) => {
            const f = e.target.files[0];
            if (f) onChange(f);
          }}
        />
        <label
          htmlFor={id}
          className="flex flex-col items-center justify-center w-full h-28 sm:h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer bg-gray-50 hover:bg-blue-50 hover:border-blue-400 transition-all group"
        >
          <Upload className="w-6 h-6 text-gray-400 group-hover:text-blue-500 mb-2 transition-colors" />
          <span className="text-sm text-blue-600 font-medium truncate max-w-full px-2 text-center">
            {file ? file.name : 'Choose file'}
          </span>
          <span className={`text-xs mt-1 ${required ? 'text-red-500' : 'text-gray-500'}`}>
            {required ? 'Required' : 'Optional'}
          </span>
        </label>
      </div>
    </div>
  );
}
